import { useId, type MouseEvent, type ReactNode } from "react";
import { ChevronRight } from "lucide-react";
import { Card, StatusBadge } from "@/design-system/components/ui";
import { cn } from "@/design-system/lib/utils";

/**
 * One collapsible message card for the Buyer intro and Broker intro tabs (D2,
 * J1). CONTROLLED: the tab owns which cards are open, because the default-open
 * rule (D4), Expand all / Collapse all (D5), the progress strip (D8) and the
 * collapse-and-advance after an action (J6) all decide it from outside.
 *
 * <p><b>The header is always visible</b>: title · ⓘ · recipient · one pill ·
 * the card's action (only while collapsed, J5) · chevron. The toggle is a real
 * <c>&lt;button&gt;</c> carrying <c>aria-expanded</c> and <c>aria-controls</c>;
 * a click anywhere else on the header row toggles too, except on the action or
 * the ⓘ, which stop the click so using them never folds the card.</p>
 *
 * <p>🔴 <b>Collapsing never discards anything.</b> The body stays MOUNTED under
 * <c>hidden</c>, so unsaved edits, a pending "Copied — click RECORD SEND…",
 * counters and in-flight requests all survive a collapse. Do not change this to
 * conditional rendering: the textareas below hold the only copy of typed
 * text.</p>
 */
export function CollapsibleCard({
  open,
  onOpenChange,
  title,
  info,
  recipient,
  pill,
  action,
  testId,
  children,
}: {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  title: ReactNode;
  /** The ⓘ beside the title (D6) — an `InfoHint`. */
  info?: ReactNode;
  /** "To the buyer" / "To the listing broker". */
  recipient?: string;
  /** Exactly one status pill (D3). */
  pill?: ReactNode;
  /** The card's one action for its state. Rendered only while COLLAPSED (J5):
   *  expanded, the body's own buttons are the only actions. */
  action?: ReactNode;
  testId: string;
  children: ReactNode;
}) {
  const bodyId = useId();
  const toggle = () => onOpenChange(!open);
  const stop = (e: MouseEvent) => e.stopPropagation();

  return (
    <Card data-testid={testId} data-open={open ? "true" : "false"}>
      <div className="grid gap-space-3">
        {/* The row click is a convenience for the mouse; the button below is
            the control, so keyboard and screen-reader users lose nothing. */}
        <header
          className="flex flex-wrap items-center gap-x-space-2 gap-y-space-2 cursor-pointer min-w-0"
          onClick={toggle}
        >
          <h3 className="text-body font-semibold m-0 min-w-0">
            <button
              type="button"
              aria-expanded={open}
              aria-controls={bodyId}
              onClick={(e) => {
                e.stopPropagation();
                toggle();
              }}
              className="text-left break-words focus-visible:outline-none focus-visible:shadow-goldRing rounded-sm"
              data-testid={`${testId}-toggle`}
            >
              {title}
            </button>
          </h3>
          {info ? (
            <span className="inline-flex" onClick={stop}>
              {info}
            </span>
          ) : null}
          {recipient ? <StatusBadge tone="info">{recipient}</StatusBadge> : null}
          {pill ? <span data-testid={`${testId}-pill`}>{pill}</span> : null}
          <span className="ml-auto flex flex-wrap items-center gap-space-2">
            {!open && action ? (
              <span className="inline-flex" onClick={stop} data-testid={`${testId}-action`}>
                {action}
              </span>
            ) : null}
            <ChevronRight
              size={16}
              strokeWidth={1.5}
              aria-hidden
              className={cn("text-gray-mid transition-transform", open && "rotate-90")}
            />
          </span>
        </header>

        <div id={bodyId} hidden={!open} data-testid={`${testId}-content`}>
          {children}
        </div>
      </div>
    </Card>
  );
}
